import { AppError } from '../core';
import { ErrorReportContext } from './error-report-context';

export interface RedactionRule {
  pattern: RegExp;
  replacement: string;
}

// Known credentials only. Users still review the report for other personal data.
export const DEFAULT_REDACTION_RULES: RedactionRule[] = [
  { pattern: /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/-]+=*/gi, replacement: '$1 [REDACTED]' },
  { pattern: /\beyJ[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+){1,2}/g, replacement: '[REDACTED_JWT]' },
  {
    pattern: /((?:["']?)(?:api[_-]?key|access[_-]?token|refresh[_-]?token|id[_-]?token|client[_-]?secret|secret|password|passwd|pwd|authorization|auth[_-]?token|cookie|session[_-]?id)(?:["']?)\s*[=:]\s*)(?:\[REDACTED(?:_[A-Z]+)?\]|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^\s,;}\]&]+)/gi,
    replacement: '$1[REDACTED]',
  },
  { pattern: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, replacement: '[REDACTED_EMAIL]' },
];

/** Drop query, fragment and embedded credentials, including URLs inside stacks/comments. */
export function sanitizeReportUrl(value: string): string {
  const path = value.split(/[?#]/, 1)[0];
  try {
    const url = new URL(path);
    if (!['https:', 'http:'].includes(url.protocol)) return '[REDACTED_URL]';
    url.username = '';
    url.password = '';
    return url.toString();
  } catch {
    return path;
  }
}

export function redactText(text: string, rules: RedactionRule[] = DEFAULT_REDACTION_RULES): string {
  const withoutUrlData = text.replace(/(?:https?:\/\/|\/)[^\s<>"'`]+/gi, value => {
    // Keep source line/column and surrounding punctuation outside a stack URL.
    const suffix = value.match(/(?::\d+){1,2}[),.;]?$|[),.;]$/)?.[0] ?? '';
    return sanitizeReportUrl(suffix ? value.slice(0, -suffix.length) : value) + suffix;
  });
  return rules.reduce((value, rule) => value.replace(rule.pattern, rule.replacement), withoutUrlData);
}

export function redactAppError(error: AppError, rules: RedactionRule[] = DEFAULT_REDACTION_RULES, depth = 0): AppError {
  return {
    ExceptionMessage: redactText(error.ExceptionMessage, rules),
    Type: redactText(error.Type, rules),
    Details: redactText(error.Details, rules),
    InnerException: error.InnerException && depth < 7
      ? redactAppError(error.InnerException, rules, depth + 1)
      : undefined,
  };
}

/** Explicit allowlist; contact email deliberately does not pass through this collector. */
export function redactErrorContext(context: ErrorReportContext, rules: RedactionRule[] = DEFAULT_REDACTION_RULES): ErrorReportContext {
  return {
    ...(context.kind ? { kind: context.kind } : {}),
    ...(context.operation ? { operation: {
      operation: redactText(context.operation.operation, rules),
      endpoint: context.operation.endpoint ? sanitizeReportUrl(context.operation.endpoint) : undefined,
      method: context.operation.method,
      operationId: context.operation.operationId ? redactText(context.operation.operationId, rules) : undefined,
      status: context.operation.status,
      requestId: context.operation.requestId ? redactText(context.operation.requestId, rules) : undefined,
      parameters: context.operation.parameters ? Object.fromEntries(Object.entries(context.operation.parameters)
        .filter(([key, value]) => /^[a-zA-Z][a-zA-Z0-9_.-]{0,63}$/.test(key) &&
          !/token|secret|password|authorization|cookie|base64/i.test(key) &&
          (value === null || ['string', 'number', 'boolean'].includes(typeof value)))
        .map(([key, value]) => [key, typeof value === 'string' ? redactText(value, rules) : value])) : undefined,
      files: context.operation.files?.map(file => ({ name: file.name, blob: file.blob, inputIndex: file.inputIndex })),
    } } : {}),
    appName: redactText(context.appName, rules),
    appVersion: context.appVersion ? redactText(context.appVersion, rules) : null,
    isProduction: context.isProduction,
    severity: context.severity,
    timestamp: context.timestamp,
    online: context.online,
    userAgent: redactText(context.userAgent, rules),
    type: redactText(context.type, rules),
    message: redactText(context.message, rules),
    pageUrl: redactText(sanitizeReportUrl(context.pageUrl), rules),
    routePath: context.routePath ? redactText(sanitizeReportUrl(context.routePath), rules) : undefined,
    errors: context.errors.slice(0, 8).map(error => redactAppError(error, rules)),
  };
}
